import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import CourseChips from './parts/CourseChips';
import ViewStudyMaterialModal from './parts/ViewStudyMaterialModal';

interface StudyMaterialFile {
  file_name: string;
  key: string;
  url: string;
  size: number;
  mime_type: string;
  uploaded_at: string;
}

interface StudyMaterial {
  id: string;
  chapter_id: string;
  module_id: string;
  course_id: string;
  institute_id: string;
  title: string;
  body: string;
  status: string;
  is_course_module_active: boolean;
  files: StudyMaterialFile[];
  video_links: string[];
  files_count?: number;
  videos_count?: number;
  created_at: string;
  created_by: string;
}

interface Chapter {
  id: string;
  module_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  study_materials?: StudyMaterial[];
  materials_count: number;
}

interface Module {
  id: string;
  course_id: string;
  title: string;
  description: string;
  order: number;
  status: string;
  chapters: Chapter[];
  chapters_count: number;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

const StudyMaterials: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [togglingModuleId, setTogglingModuleId] = useState<string | null>(null);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [chapterMaterials, setChapterMaterials] = useState<{ [key: string]: StudyMaterial[] }>({});
  const [loadingChapterMaterials, setLoadingChapterMaterials] = useState<Set<string>>(new Set());

  const { profile, loading: profileLoading } = useOperatorProfileStore();

  useEffect(() => {
    if (selectedCourseId && profile?.institute_id) {
      fetchCourseStructure();
    } else {
      setModules([]);
    }
  }, [selectedCourseId, profile]);

  const fetchCourseStructure = async () => {
    if (!profile?.institute_id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/study-materials/course/${selectedCourseId}/structure`);
      if (response.data.success) {
        setModules(response.data.data.modules || []);
        if (response.data.data.modules?.length > 0) {
          const firstModule = response.data.data.modules[0];
          setExpandedModules(new Set([firstModule.id]));
          if (firstModule.chapters?.length > 0) {
            setExpandedChapters(new Set([firstModule.chapters[0].id]));
          }
        }
      } else { setError('Failed to load course structure'); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load course structure');
    } finally { setLoading(false); }
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
  };

  const handleViewDetails = (material: StudyMaterial) => {
    setSelectedMaterial(material);
    setShowDetailsModal(true);
  };

  const handleToggleModule = async (materialId: string, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    setTogglingModuleId(materialId);
    try {
      const response = await apiClient.patch(
        `/v1/study-materials/${materialId}/toggle-module`,
        { is_course_module_active: !currentStatus }
      );
      if (response.data.success) {
        setChapterMaterials(prev => {
          const newState = { ...prev };
          Object.keys(newState).forEach(chapterId => {
            newState[chapterId] = newState[chapterId].map(material =>
              material.id === materialId
                ? { ...material, is_course_module_active: !currentStatus }
                : material
            );
          });
          return newState;
        });
        toast.current?.show({ severity: 'success', summary: 'Success', detail: `Study material ${!currentStatus ? 'added to' : 'removed from'} course modules`, life: 3000 });
      } else {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: response.data.error || 'Failed to update module status', life: 4000 });
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.response?.data?.error || 'Failed to update module status', life: 4000 });
    } finally { setTogglingModuleId(null); }
  };

  const toggleModuleExpansion = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      newSet.has(moduleId) ? newSet.delete(moduleId) : newSet.add(moduleId);
      return newSet;
    });
  };

  const toggleChapterExpansion = async (chapterId: string) => {
    const isCurrentlyExpanded = expandedChapters.has(chapterId);
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      newSet.has(chapterId) ? newSet.delete(chapterId) : newSet.add(chapterId);
      return newSet;
    });
    if (!isCurrentlyExpanded && !chapterMaterials[chapterId]) {
      await fetchChapterMaterials(chapterId);
    }
  };

  const fetchChapterMaterials = async (chapterId: string) => {
    if (loadingChapterMaterials.has(chapterId)) return;
    setLoadingChapterMaterials(prev => { const s = new Set(prev); s.add(chapterId); return s; });
    try {
      const response = await apiClient.get(`/v1/study-materials/chapter/${chapterId}?status=active`);
      if (response.data.success) {
        setChapterMaterials(prev => ({ ...prev, [chapterId]: response.data.data || [] }));
      }
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load study materials', life: 3000 });
    } finally {
      setLoadingChapterMaterials(prev => { const s = new Set(prev); s.delete(chapterId); return s; });
    }
  };

  const getTotalMaterialsCount = () =>
    modules.reduce((total, module) =>
      total + module.chapters.reduce((ct, chapter) => ct + (chapter.materials_count || 0), 0), 0
    );

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateString; }
  };

  // ── Guard: profile loading ────────────────────────────────────────────────
  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading profile data...</p>
      </div>
    );
  }

  if (!profile?.institute_id) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="warn" text="Unable to load profile data. Please refresh the page." style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />

      {/* ── Hero Header ───────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: 'white', position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)'
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '4px 12px', background: 'rgba(251,178,31,0.18)',
                border: '1px solid rgba(251,178,31,0.35)', borderRadius: '20px',
                marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700',
                letterSpacing: '0.8px', textTransform: 'uppercase', color: '#fbb21f'
              }}>
                <i className="pi pi-book" style={{ fontSize: '0.7rem' }}></i>
                Study Material Library
              </div>
              <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
                Study Materials
              </h1>
              <p style={{ margin: '0 0 12px 0', color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
                Browse and access educational resources
              </p>
              <div style={{ display: 'flex', gap: '16px', fontSize: '0.83rem', color: 'rgba(255,255,255,0.65)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="pi pi-book" style={{ fontSize: '0.8rem', color: '#fbb21f' }} />
                  <span style={{ color: selectedCourseId ? '#fbb21f' : 'inherit', fontWeight: selectedCourseId ? '600' : '400' }}>
                    {selectedCourseId
                      ? `${getTotalMaterialsCount()} Materials in ${modules.length} Modules`
                      : 'Select a course to begin'}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Course Filter Card ────────────────────────────────────────────── */}
      <div style={{
        background: 'white', borderRadius: '16px', padding: '20px 24px',
        marginBottom: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="pi pi-filter" style={{ fontSize: '0.8rem' }}></i>
            Select Course to View Study Materials
          </span>
          {selectedCourseId && (
            <button
              onClick={() => setSelectedCourseId('')}
              style={{
                marginLeft: 'auto', padding: '6px 12px',
                background: 'transparent', border: '1px solid #E5E7EB',
                borderRadius: '8px', color: '#6b7280',
                fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer',
                transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '6px'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#E8EEF7'; e.currentTarget.style.borderColor = '#fbb21f'; e.currentTarget.style.color = '#0e225d'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6b7280'; }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.72rem' }}></i>
              Clear Selection
            </button>
          )}
        </div>
        <CourseChips
          selectedCourseId={selectedCourseId}
          onCourseSelect={handleCourseSelect}
          showSearch={false}
        />
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />}

      {/* ── Empty: no course selected ─────────────────────────────────────── */}
      {!selectedCourseId ? (
        <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #E5E7EB' }}>
          <div style={{ width: '64px', height: '64px', background: 'rgba(14,34,93,0.06)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <i className="pi pi-book" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }}></i>
          </div>
          <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '1.1rem', fontWeight: '700' }}>Select a Course</h3>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>Click on a course chip above to view study materials</p>
        </div>

      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB' }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
            <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>Loading study materials...</p>
          </div>
        </div>

      ) : modules.length === 0 ? (
        <div style={{ padding: '80px 20px', textAlign: 'center', background: 'white', borderRadius: '16px', border: '2px dashed #E5E7EB' }}>
          <div style={{ width: '64px', height: '64px', background: 'rgba(14,34,93,0.06)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <i className="pi pi-book" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }}></i>
          </div>
          <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '1.1rem', fontWeight: '700' }}>No Study Materials Found</h3>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>This course has no study materials yet</p>
        </div>

      ) : (
        /* ── Modules accordion ─────────────────────────────────────────── */
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' }}>
          {/* Toolbar accent */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
            <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>Course Structure</h2>
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#9ca3af', fontWeight: '500' }}>
              {modules.length} module{modules.length !== 1 ? 's' : ''} · {getTotalMaterialsCount()} material{getTotalMaterialsCount() !== 1 ? 's' : ''}
            </span>
          </div>

          {modules.map((module, moduleIdx) => {
            const isModuleExpanded = expandedModules.has(module.id);

            return (
              <div key={module.id} style={{ marginBottom: '12px' }}>

                {/* ── Module Header — navy gradient ───────────────────── */}
                <div
                  onClick={() => toggleModuleExpansion(module.id)}
                  style={{
                    padding: '18px 20px',
                    background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                    borderRadius: isModuleExpanded ? '12px 12px 0 0' : '12px',
                    cursor: 'pointer',
                    transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                    position: 'relative', overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(14,34,93,0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 18px rgba(14,34,93,0.28)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.2)';
                  }}
                >
                  {/* Orb */}
                  <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(15px)', pointerEvents: 'none' }} />

                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', position: 'relative', zIndex: 1 }}>
                    {/* Order badge */}
                    <div style={{
                      width: '38px', height: '38px', borderRadius: '10px',
                      background: 'rgba(251,178,31,0.2)', border: '1px solid rgba(251,178,31,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.9rem', fontWeight: '700', color: '#fbb21f', flexShrink: 0
                    }}>
                      {module.order}
                    </div>

                    <div style={{ flex: 1 }}>
                      <h3 style={{ margin: '0 0 3px 0', fontSize: '1rem', fontWeight: '700', color: 'white' }}>
                        {module.title}
                      </h3>
                      {module.description && (
                        <p style={{ margin: '0 0 6px 0', fontSize: '0.78rem', color: 'rgba(255,255,255,0.6)', lineHeight: '1.4' }}>
                          {module.description}
                        </p>
                      )}
                      <span style={{ fontSize: '0.7rem', color: '#fbb21f', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <i className="pi pi-book" style={{ fontSize: '0.65rem' }}></i>
                        {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
                      </span>
                    </div>

                    <i className={`pi ${isModuleExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                      style={{ color: '#fbb21f', fontSize: '0.9rem', transition: 'transform 0.3s ease' }}
                    ></i>
                  </div>
                </div>

                {/* ── Chapters (Expanded) ─────────────────────────────── */}
                {isModuleExpanded && (
                  <div style={{
                    border: '1px solid #E5E7EB', borderTop: 'none',
                    borderRadius: '0 0 12px 12px', padding: '16px',
                    background: 'rgba(14,34,93,0.02)'
                  }}>
                    {module.chapters.map((chapter) => {
                      const isChapterExpanded = expandedChapters.has(chapter.id);

                      return (
                        <div key={chapter.id} style={{ marginBottom: '10px' }}>

                          {/* Chapter Header */}
                          <div
                            onClick={() => toggleChapterExpansion(chapter.id)}
                            style={{
                              padding: '14px 16px',
                              background: isChapterExpanded ? '#E8EEF7' : 'white',
                              border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : '#E5E7EB'}`,
                              borderRadius: isChapterExpanded ? '10px 10px 0 0' : '10px',
                              cursor: 'pointer', transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isChapterExpanded) {
                                e.currentTarget.style.background = '#F5F7FA';
                                e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChapterExpanded) {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.borderColor = '#E5E7EB';
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              {/* Chapter order badge */}
                              <div style={{
                                width: '32px', height: '32px', borderRadius: '8px',
                                background: isChapterExpanded ? 'rgba(14,34,93,0.12)' : '#F5F7FA',
                                border: `1px solid ${isChapterExpanded ? 'rgba(14,34,93,0.2)' : '#E5E7EB'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.75rem', fontWeight: '700', color: '#0e225d', flexShrink: 0
                              }}>
                                {module.order}.{chapter.order}
                              </div>

                              <div style={{ flex: 1 }}>
                                <h4 style={{ margin: '0 0 2px 0', fontSize: '0.9rem', fontWeight: '600', color: '#0e225d' }}>
                                  {chapter.title}
                                </h4>
                                {chapter.description && (
                                  <p style={{ margin: '0 0 4px 0', fontSize: '0.73rem', color: '#6b7280' }}>
                                    {chapter.description}
                                  </p>
                                )}
                                <span style={{ fontSize: '0.68rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <i className="pi pi-file" style={{ fontSize: '0.62rem' }}></i>
                                  {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
                                </span>
                              </div>

                              <i className={`pi ${isChapterExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                                style={{ color: '#0e225d', fontSize: '0.82rem', opacity: 0.6, transition: 'transform 0.25s ease' }}
                              ></i>
                            </div>
                          </div>

                          {/* ── Study Materials ────────────────────────── */}
                          {isChapterExpanded && (
                            <div style={{
                              border: '1px solid rgba(14,34,93,0.15)', borderTop: 'none',
                              borderRadius: '0 0 10px 10px', padding: '12px',
                              background: 'white'
                            }}>
                              {loadingChapterMaterials.has(chapter.id) ? (
                                <div style={{ padding: '24px', textAlign: 'center' }}>
                                  <ProgressSpinner style={{ width: '32px', height: '32px' }} strokeWidth="4" animationDuration=".8s" />
                                  <p style={{ margin: '10px 0 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>Loading materials...</p>
                                </div>
                              ) : chapterMaterials[chapter.id]?.length > 0 ? (
                                chapterMaterials[chapter.id].map((material) => (
                                  <div
                                    key={material.id}
                                    onClick={() => handleViewDetails(material)}
                                    style={{
                                      padding: '14px 16px',
                                      background: 'white',
                                      border: '1px solid #E5E7EB',
                                      borderRadius: '10px',
                                      marginBottom: '8px',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.transform = 'translateX(4px)';
                                      e.currentTarget.style.borderColor = 'rgba(251,178,31,0.4)';
                                      e.currentTarget.style.boxShadow = '0 2px 10px rgba(14,34,93,0.08)';
                                      e.currentTarget.style.background = 'rgba(14,34,93,0.01)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.transform = 'translateX(0)';
                                      e.currentTarget.style.borderColor = '#E5E7EB';
                                      e.currentTarget.style.boxShadow = 'none';
                                      e.currentTarget.style.background = 'white';
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                      {/* Material icon — navy */}
                                      <div style={{
                                        width: '36px', height: '36px', borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                        boxShadow: '0 2px 8px rgba(14,34,93,0.2)'
                                      }}>
                                        <i className="pi pi-book" style={{ color: '#fbb21f', fontSize: '0.9rem' }}></i>
                                      </div>

                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <h5 style={{ margin: '0 0 4px 0', fontSize: '0.88rem', fontWeight: '600', color: '#0e225d' }}>
                                          {material.title}
                                        </h5>
                                        <p style={{
                                          margin: '0 0 8px 0', fontSize: '0.78rem', color: '#6b7280', lineHeight: '1.45',
                                          display: '-webkit-box', WebkitLineClamp: 2,
                                          WebkitBoxOrient: 'vertical', overflow: 'hidden'
                                        }}>
                                          {material.body}
                                        </p>

                                        {/* Meta row */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', fontSize: '0.68rem', color: '#9ca3af' }}>
                                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            <i className="pi pi-calendar" style={{ fontSize: '0.62rem' }}></i>
                                            {formatDate(material.created_at)}
                                          </span>
                                          {material.files.length > 0 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#0e225d', fontWeight: '600' }}>
                                              <i className="pi pi-paperclip" style={{ fontSize: '0.62rem' }}></i>
                                              {material.files.length} {material.files.length === 1 ? 'file' : 'files'}
                                            </span>
                                          )}
                                          {material.video_links.length > 0 && (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', color: '#b8860b', fontWeight: '600' }}>
                                              <i className="pi pi-video" style={{ fontSize: '0.62rem' }}></i>
                                              {material.video_links.length} {material.video_links.length === 1 ? 'video' : 'videos'}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Toggle module button */}
                                      <button
                                        onClick={(e) => handleToggleModule(material.id, material.is_course_module_active ?? false, e)}
                                        disabled={togglingModuleId === material.id}
                                        title={material.is_course_module_active ? 'Remove from Course Modules' : 'Add to Course Modules'}
                                        style={{
                                          padding: '6px 12px',
                                          background: material.is_course_module_active
                                            ? 'rgba(22,163,74,0.12)'
                                            : 'rgba(14,34,93,0.06)',
                                          color: material.is_course_module_active ? '#15803d' : '#0e225d',
                                          border: `1px solid ${material.is_course_module_active ? 'rgba(22,163,74,0.3)' : 'rgba(14,34,93,0.2)'}`,
                                          borderRadius: '8px',
                                          cursor: togglingModuleId === material.id ? 'not-allowed' : 'pointer',
                                          fontSize: '0.7rem', fontWeight: '700',
                                          transition: 'all 0.2s ease',
                                          display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0,
                                          opacity: togglingModuleId === material.id ? 0.6 : 1
                                        }}
                                        onMouseEnter={(e) => {
                                          if (togglingModuleId !== material.id) {
                                            e.currentTarget.style.transform = 'scale(1.03)';
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                      >
                                        {togglingModuleId === material.id ? (
                                          <>
                                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.65rem' }}></i>
                                            Updating...
                                          </>
                                        ) : (
                                          <>
                                            <i className={`pi ${material.is_course_module_active ? 'pi-check-circle' : 'pi-circle'}`} style={{ fontSize: '0.65rem' }}></i>
                                            {material.is_course_module_active ? 'Module' : 'Add'}
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                /* Empty chapter */
                                <div style={{ padding: '24px', textAlign: 'center' }}>
                                  <div style={{ width: '44px', height: '44px', background: 'rgba(14,34,93,0.05)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
                                    <i className="pi pi-inbox" style={{ fontSize: '1.2rem', color: '#0e225d', opacity: 0.25 }}></i>
                                  </div>
                                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af' }}>No study materials in this chapter</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Details Modal ────────────────────────────────────────────────────── */}
      <ViewStudyMaterialModal
        visible={showDetailsModal}
        material={selectedMaterial}
        onHide={() => setShowDetailsModal(false)}
      />
    </div>
  );
};

export default StudyMaterials;

// import React, { useState, useEffect, useRef } from 'react';
// import { Toast } from 'primereact/toast';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Message } from 'primereact/message';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
// import CourseChips from './parts/CourseChips';
// import ViewStudyMaterialModal from './parts/ViewStudyMaterialModal';

// interface StudyMaterialFile {
//   file_name: string;
//   key: string;
//   url: string;
//   size: number;
//   mime_type: string;
//   uploaded_at: string;
// }

// interface StudyMaterial {
//   id: string;
//   chapter_id: string;
//   module_id: string;
//   course_id: string;
//   institute_id: string;
//   title: string;
//   body: string;
//   status: string;
//   is_course_module_active: boolean;
//   files: StudyMaterialFile[];
//   video_links: string[];
//   files_count?: number;
//   videos_count?: number;
//   created_at: string;
//   created_by: string;
// }

// interface Chapter {
//   id: string;
//   module_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   study_materials?: StudyMaterial[];
//   materials_count: number;
// }

// interface Module {
//   id: string;
//   course_id: string;
//   title: string;
//   description: string;
//   order: number;
//   status: string;
//   chapters: Chapter[];
//   chapters_count: number;
// }

// interface Course {
//   course_id: string;
//   name: string;
//   title: string;
//   status: string;
// }

// const StudyMaterials: React.FC = () => {
//   const toast = useRef<Toast>(null);
//   const [modules, setModules] = useState<Module[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [selectedCourseId, setSelectedCourseId] = useState<string>('');
//   const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);
//   const [showDetailsModal, setShowDetailsModal] = useState(false);
//   const [togglingModuleId, setTogglingModuleId] = useState<string | null>(null);
//   const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
//   const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
//   const [chapterMaterials, setChapterMaterials] = useState<{ [key: string]: StudyMaterial[] }>({});
//   const [loadingChapterMaterials, setLoadingChapterMaterials] = useState<Set<string>>(new Set());

//   // Get profile data from store
//   const { profile, loading: profileLoading } = useOperatorProfileStore();

//   useEffect(() => {
//     if (selectedCourseId && profile?.institute_id) {
//       fetchCourseStructure();
//     } else {
//       setModules([]);
//     }
//   }, [selectedCourseId, profile]);

//   const fetchCourseStructure = async () => {
//     if (!profile?.institute_id) return;

//     try {
//       setLoading(true);
//       setError(null);

//       const response = await apiClient.get(
//         `/v1/study-materials/course/${selectedCourseId}/structure`
//       );

//       if (response.data.success) {
//         setModules(response.data.data.modules || []);
        
//         // Auto-expand first module and first chapter for better UX
//         if (response.data.data.modules && response.data.data.modules.length > 0) {
//           const firstModule = response.data.data.modules[0];
//           setExpandedModules(new Set([firstModule.id]));
          
//           if (firstModule.chapters && firstModule.chapters.length > 0) {
//             setExpandedChapters(new Set([firstModule.chapters[0].id]));
//           }
//         }
//       } else {
//         setError('Failed to load course structure');
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch course structure:', err);
//       setError(err.response?.data?.error || 'Failed to load course structure');
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleCourseSelect = (course: Course) => {
//     setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
//   };

//   const handleViewDetails = (material: StudyMaterial) => {
//     setSelectedMaterial(material);
//     setShowDetailsModal(true);
//   };

//   const handleToggleModule = async (materialId: string, currentStatus: boolean, e: React.MouseEvent) => {
//     e.stopPropagation();
    
//     setTogglingModuleId(materialId);
    
//     try {
//       const response = await apiClient.patch(
//         `/v1/study-materials/${materialId}/toggle-module`,
//         { is_course_module_active: !currentStatus }
//       );

//       if (response.data.success) {
//         // Update the material in chapterMaterials state
//         setChapterMaterials(prev => {
//           const newState = { ...prev };
//           Object.keys(newState).forEach(chapterId => {
//             newState[chapterId] = newState[chapterId].map(material =>
//               material.id === materialId
//                 ? { ...material, is_course_module_active: !currentStatus }
//                 : material
//             );
//           });
//           return newState;
//         });

//         toast.current?.show({
//           severity: 'success',
//           summary: 'Success',
//           detail: `Study material ${!currentStatus ? 'added to' : 'removed from'} course modules`,
//           life: 3000
//         });
//       } else {
//         toast.current?.show({
//           severity: 'error',
//           summary: 'Error',
//           detail: response.data.error || 'Failed to update module status',
//           life: 4000
//         });
//       }
//     } catch (err: any) {
//       console.error('Failed to toggle module status:', err);
//       toast.current?.show({
//         severity: 'error',
//         summary: 'Error',
//         detail: err.response?.data?.error || 'Failed to update module status',
//         life: 4000
//       });
//     } finally {
//       setTogglingModuleId(null);
//     }
//   };

//   const toggleModuleExpansion = (moduleId: string) => {
//     setExpandedModules(prev => {
//       const newSet = new Set(prev);
//       if (newSet.has(moduleId)) {
//         newSet.delete(moduleId);
//       } else {
//         newSet.add(moduleId);
//       }
//       return newSet;
//     });
//   };

//   const toggleChapterExpansion = async (chapterId: string) => {
//     const isCurrentlyExpanded = expandedChapters.has(chapterId);
    
//     setExpandedChapters(prev => {
//       const newSet = new Set(prev);
//       if (newSet.has(chapterId)) {
//         newSet.delete(chapterId);
//       } else {
//         newSet.add(chapterId);
//       }
//       return newSet;
//     });

//     // Fetch materials if expanding and not already loaded
//     if (!isCurrentlyExpanded && !chapterMaterials[chapterId]) {
//       await fetchChapterMaterials(chapterId);
//     }
//   };

//   const fetchChapterMaterials = async (chapterId: string) => {
//     if (loadingChapterMaterials.has(chapterId)) return; // Already loading

//     setLoadingChapterMaterials(prev => {
//       const newSet = new Set(prev);
//       newSet.add(chapterId);
//       return newSet;
//     });

//     try {
//       const response = await apiClient.get(`/v1/study-materials/chapter/${chapterId}?status=active`);

//       if (response.data.success) {
//         setChapterMaterials(prev => ({
//           ...prev,
//           [chapterId]: response.data.data || []
//         }));
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch chapter materials:', err);
//       toast.current?.show({
//         severity: 'error',
//         summary: 'Error',
//         detail: 'Failed to load study materials',
//         life: 3000
//       });
//     } finally {
//       setLoadingChapterMaterials(prev => {
//         const newSet = new Set(prev);
//         newSet.delete(chapterId);
//         return newSet;
//       });
//     }
//   };

//   const getTotalMaterialsCount = () => {
//     return modules.reduce((total, module) => 
//       total + module.chapters.reduce((chapterTotal, chapter) => 
//         chapterTotal + (chapter.materials_count || 0), 0
//       ), 0
//     );
//   };

//   const formatDate = (dateString: string) => {
//     try {
//       const date = new Date(dateString);
//       return date.toLocaleDateString('en-IN', {
//         day: '2-digit',
//         month: 'short',
//         year: 'numeric',
//         hour: '2-digit',
//         minute: '2-digit'
//       });
//     } catch {
//       return dateString;
//     }
//   };

//   if (profileLoading) {
//     return (
//       <div style={{
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         height: '400px',
//         flexDirection: 'column',
//         gap: '16px'
//       }}>
//         <ProgressSpinner
//           style={{ width: '50px', height: '50px' }}
//           strokeWidth="3"
//         />
//         <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading profile data...</p>
//       </div>
//     );
//   }

//   if (!profile?.institute_id) {
//     return (
//       <div style={{ padding: '24px' }}>
//         <Message
//           severity="warn"
//           text="Unable to load profile data. Please refresh the page."
//           style={{ fontSize: '0.9rem' }}
//         />
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
//       <Toast ref={toast} />

//       {/* Header */}
//       <div style={{
//         background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//         borderRadius: '20px',
//         padding: '32px',
//         marginBottom: '24px',
//         color: 'white',
//         position: 'relative',
//         overflow: 'hidden',
//         boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
//       }}>
//         <div style={{
//           position: 'absolute',
//           top: '-50px',
//           right: '-50px',
//           width: '200px',
//           height: '200px',
//           background: 'rgba(212, 241, 126, 0.1)',
//           borderRadius: '50%',
//           filter: 'blur(40px)'
//         }} />

//         <div style={{ position: 'relative', zIndex: 1 }}>
//           <div style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'flex-start',
//             flexWrap: 'wrap',
//             gap: '16px'
//           }}>
//             <div>
//               <div style={{
//                 display: 'inline-block',
//                 padding: '5px 12px',
//                 background: 'rgba(212, 241, 126, 0.2)',
//                 borderRadius: '20px',
//                 marginBottom: '10px',
//                 fontSize: '0.7rem',
//                 fontWeight: '600',
//                 letterSpacing: '0.5px',
//                 textTransform: 'uppercase'
//               }}>
//                 Study Material Library
//               </div>
//               <h1 style={{
//                 margin: '0 0 8px 0',
//                 fontSize: '1.7rem',
//                 fontWeight: '700',
//                 color: 'white',
//                 letterSpacing: '-0.5px'
//               }}>
//                 Study Materials
//               </h1>
//               <p style={{
//                 margin: '0',
//                 color: 'rgba(255,255,255,0.8)',
//                 fontSize: '0.9rem'
//               }}>
//                 Browse and access educational resources
//               </p>
//               <div style={{
//                 marginTop: '12px',
//                 display: 'flex',
//                 gap: '16px',
//                 fontSize: '0.85rem',
//                 color: 'rgba(255,255,255,0.7)'
//               }}>
//                 <span>
//                   <i className="pi pi-book" style={{ marginRight: '6px' }}></i>
//                   {selectedCourseId ? `${getTotalMaterialsCount()} Materials in ${modules.length} Modules` : 'Select a course'}
//                 </span>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Course Chips Filter */}
//       <div style={{
//         background: 'white',
//         borderRadius: '16px',
//         padding: '24px',
//         marginBottom: '24px',
//         boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
//         border: '1px solid #f0f0f0'
//       }}>
//         <div style={{
//           display: 'flex',
//           alignItems: 'center',
//           gap: '12px',
//           marginBottom: '16px'
//         }}>
//           <i className="pi pi-filter" style={{ color: '#0f3d3e', fontSize: '1rem' }}></i>
//           <h3 style={{
//             margin: 0,
//             fontSize: '0.95rem',
//             fontWeight: '600',
//             color: '#1a1a1a'
//           }}>
//             Select Course to View Study Materials
//           </h3>
//           {selectedCourseId && (
//             <button
//               onClick={() => setSelectedCourseId('')}
//               style={{
//                 marginLeft: 'auto',
//                 padding: '6px 12px',
//                 background: 'transparent',
//                 border: '1px solid #e0e0e0',
//                 borderRadius: '8px',
//                 color: '#666',
//                 fontSize: '0.8rem',
//                 fontWeight: '600',
//                 cursor: 'pointer',
//                 transition: 'all 0.2s ease',
//                 display: 'flex',
//                 alignItems: 'center',
//                 gap: '6px'
//               }}
//               onMouseEnter={(e) => {
//                 e.currentTarget.style.background = '#f5f5f5';
//                 e.currentTarget.style.borderColor = '#0f3d3e';
//               }}
//               onMouseLeave={(e) => {
//                 e.currentTarget.style.background = 'transparent';
//                 e.currentTarget.style.borderColor = '#e0e0e0';
//               }}
//             >
//               <i className="pi pi-times" style={{ fontSize: '0.75rem' }}></i>
//               Clear Selection
//             </button>
//           )}
//         </div>
//         <CourseChips
//           selectedCourseId={selectedCourseId}
//           onCourseSelect={handleCourseSelect}
//           showSearch={false}
//         />
//       </div>

//       {error && (
//         <Message
//           severity="error"
//           text={error}
//           style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
//         />
//       )}

//       {/* Content Display */}
//       {!selectedCourseId ? (
//         <div style={{
//           padding: '80px 20px',
//           textAlign: 'center',
//           background: 'white',
//           borderRadius: '16px',
//           border: '2px dashed #e0e0e0'
//         }}>
//           <i className="pi pi-book" style={{ fontSize: '3.5rem', color: '#ddd', marginBottom: '16px' }}></i>
//           <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
//             Select a Course
//           </h3>
//           <p style={{ margin: '0', color: '#999', fontSize: '0.9rem' }}>
//             Click on a course chip above to view study materials
//           </p>
//         </div>
//       ) : loading ? (
//         <div style={{
//           display: 'flex',
//           justifyContent: 'center',
//           alignItems: 'center',
//           minHeight: '400px',
//           background: 'white',
//           borderRadius: '16px'
//         }}>
//           <div style={{ textAlign: 'center' }}>
//             <ProgressSpinner
//               style={{ width: '50px', height: '50px' }}
//               strokeWidth="3"
//             />
//             <p style={{
//               marginTop: '16px',
//               color: '#0f3d3e',
//               fontSize: '0.95rem'
//             }}>
//               Loading study materials...
//             </p>
//           </div>
//         </div>
//       ) : (
//         <>
//           {modules.length === 0 ? (
//             <div style={{
//               padding: '80px 20px',
//               textAlign: 'center',
//               background: 'white',
//               borderRadius: '16px',
//               border: '2px dashed #e0e0e0'
//             }}>
//               <i className="pi pi-book" style={{ fontSize: '3rem', color: '#ddd', marginBottom: '16px' }}></i>
//               <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
//                 No study materials found
//               </h3>
//               <p style={{ margin: '0', color: '#999', fontSize: '0.9rem' }}>
//                 This course has no study materials yet
//               </p>
//             </div>
//           ) : (
//             <div style={{
//               background: 'white',
//               borderRadius: '16px',
//               padding: '24px',
//               boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
//               border: '1px solid #f0f0f0'
//             }}>
//               {/* Modules List */}
//               {modules.map((module) => (
//                 <div key={module.id} style={{ marginBottom: '16px' }}>
//                   {/* Module Header */}
//                   <div
//                     onClick={() => toggleModuleExpansion(module.id)}
//                     style={{
//                       padding: '20px',
//                       background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
//                       borderRadius: '12px',
//                       cursor: 'pointer',
//                       transition: 'all 0.2s ease',
//                       marginBottom: expandedModules.has(module.id) ? '12px' : '0'
//                     }}
//                     onMouseEnter={(e) => {
//                       e.currentTarget.style.transform = 'translateY(-1px)';
//                       e.currentTarget.style.boxShadow = '0 4px 12px rgba(15,61,62,0.2)';
//                     }}
//                     onMouseLeave={(e) => {
//                       e.currentTarget.style.transform = 'translateY(0)';
//                       e.currentTarget.style.boxShadow = 'none';
//                     }}
//                   >
//                     <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
//                       <div style={{
//                         width: '40px',
//                         height: '40px',
//                         borderRadius: '8px',
//                         background: 'rgba(212, 241, 126, 0.2)',
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center',
//                         fontSize: '1rem',
//                         fontWeight: '700',
//                         color: '#d4f17e',
//                         flexShrink: 0
//                       }}>
//                         {module.order}
//                       </div>

//                       <div style={{ flex: 1 }}>
//                         <h3 style={{
//                           margin: '0 0 4px 0',
//                           fontSize: '1.1rem',
//                           fontWeight: '600',
//                           color: 'white'
//                         }}>
//                           {module.title}
//                         </h3>
//                         <p style={{
//                           margin: 0,
//                           fontSize: '0.8rem',
//                           color: 'rgba(255,255,255,0.7)'
//                         }}>
//                           {module.description}
//                         </p>
//                         <div style={{
//                           marginTop: '8px',
//                           fontSize: '0.75rem',
//                           color: 'rgba(212, 241, 126, 0.9)'
//                         }}>
//                           <i className="pi pi-book" style={{ marginRight: '4px' }}></i>
//                           {module.chapters_count} {module.chapters_count === 1 ? 'Chapter' : 'Chapters'}
//                         </div>
//                       </div>

//                       <i 
//                         className={`pi ${expandedModules.has(module.id) ? 'pi-chevron-up' : 'pi-chevron-down'}`}
//                         style={{ color: '#d4f17e', fontSize: '1rem' }}
//                       ></i>
//                     </div>
//                   </div>

//                   {/* Chapters (Expanded) */}
//                   {expandedModules.has(module.id) && (
//                     <div style={{ paddingLeft: '20px' }}>
//                       {module.chapters.map((chapter) => (
//                         <div key={chapter.id} style={{ marginBottom: '12px' }}>
//                           {/* Chapter Header */}
//                           <div
//                             onClick={() => toggleChapterExpansion(chapter.id)}
//                             style={{
//                               padding: '16px',
//                               background: '#f8f9fa',
//                               border: '1px solid #e0e0e0',
//                               borderRadius: '10px',
//                               cursor: 'pointer',
//                               transition: 'all 0.2s ease',
//                               marginBottom: expandedChapters.has(chapter.id) ? '8px' : '0'
//                             }}
//                             onMouseEnter={(e) => {
//                               e.currentTarget.style.background = '#f0f0f0';
//                               e.currentTarget.style.borderColor = '#0f3d3e';
//                             }}
//                             onMouseLeave={(e) => {
//                               e.currentTarget.style.background = '#f8f9fa';
//                               e.currentTarget.style.borderColor = '#e0e0e0';
//                             }}
//                           >
//                             <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//                               <div style={{
//                                 width: '32px',
//                                 height: '32px',
//                                 borderRadius: '6px',
//                                 background: '#e0e0e0',
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 justifyContent: 'center',
//                                 fontSize: '0.8rem',
//                                 fontWeight: '600',
//                                 color: '#0f3d3e',
//                                 flexShrink: 0
//                               }}>
//                                 {module.order}.{chapter.order}
//                               </div>

//                               <div style={{ flex: 1 }}>
//                                 <h4 style={{
//                                   margin: '0 0 2px 0',
//                                   fontSize: '0.95rem',
//                                   fontWeight: '600',
//                                   color: '#1a1a1a'
//                                 }}>
//                                   {chapter.title}
//                                 </h4>
//                                 <p style={{
//                                   margin: '0',
//                                   fontSize: '0.75rem',
//                                   color: '#666'
//                                 }}>
//                                   {chapter.description}
//                                 </p>
//                                 <div style={{
//                                   marginTop: '6px',
//                                   fontSize: '0.7rem',
//                                   color: '#999'
//                                 }}>
//                                   <i className="pi pi-file" style={{ marginRight: '4px' }}></i>
//                                   {chapter.materials_count} {chapter.materials_count === 1 ? 'Material' : 'Materials'}
//                                 </div>
//                               </div>

//                               <i 
//                                 className={`pi ${expandedChapters.has(chapter.id) ? 'pi-chevron-up' : 'pi-chevron-down'}`}
//                                 style={{ color: '#0f3d3e', fontSize: '0.9rem' }}
//                               ></i>
//                             </div>
//                           </div>

//                           {/* Study Materials (Expanded) */}
//                           {expandedChapters.has(chapter.id) && (
//                             <div style={{ paddingLeft: '20px', marginTop: '8px' }}>
//                               {loadingChapterMaterials.has(chapter.id) ? (
//                                 <div style={{
//                                   padding: '20px',
//                                   textAlign: 'center',
//                                   background: 'white',
//                                   border: '1px solid #e9ecef',
//                                   borderRadius: '8px'
//                                 }}>
//                                   <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
//                                   <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: '#666' }}>
//                                     Loading materials...
//                                   </p>
//                                 </div>
//                               ) : chapterMaterials[chapter.id] && chapterMaterials[chapter.id].length > 0 ? (
//                                 chapterMaterials[chapter.id].map((material) => (
//                                 <div
//                                   key={material.id}
//                                   style={{
//                                     padding: '16px',
//                                     background: 'white',
//                                     border: '1px solid #e9ecef',
//                                     borderRadius: '8px',
//                                     marginBottom: '8px',
//                                     cursor: 'pointer',
//                                     transition: 'all 0.2s ease'
//                                   }}
//                                   onClick={() => handleViewDetails(material)}
//                                   onMouseEnter={(e) => {
//                                     e.currentTarget.style.transform = 'translateX(4px)';
//                                     e.currentTarget.style.borderColor = '#0f3d3e';
//                                     e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,61,62,0.1)';
//                                   }}
//                                   onMouseLeave={(e) => {
//                                     e.currentTarget.style.transform = 'translateX(0)';
//                                     e.currentTarget.style.borderColor = '#e9ecef';
//                                     e.currentTarget.style.boxShadow = 'none';
//                                   }}
//                                 >
//                                   <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
//                                     <div style={{
//                                       width: '36px',
//                                       height: '36px',
//                                       borderRadius: '8px',
//                                       background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
//                                       display: 'flex',
//                                       alignItems: 'center',
//                                       justifyContent: 'center',
//                                       flexShrink: 0
//                                     }}>
//                                       <i className="pi pi-book" style={{ color: '#d4f17e', fontSize: '1rem' }}></i>
//                                     </div>

//                                     <div style={{ flex: 1, minWidth: 0 }}>
//                                       <h5 style={{
//                                         margin: '0 0 6px 0',
//                                         fontSize: '0.9rem',
//                                         fontWeight: '600',
//                                         color: '#1a1a1a'
//                                       }}>
//                                         {material.title}
//                                       </h5>
//                                       <p style={{
//                                         margin: '0 0 8px 0',
//                                         fontSize: '0.8rem',
//                                         color: '#666',
//                                         lineHeight: '1.4',
//                                         display: '-webkit-box',
//                                         WebkitLineClamp: 2,
//                                         WebkitBoxOrient: 'vertical',
//                                         overflow: 'hidden'
//                                       }}>
//                                         {material.body}
//                                       </p>

//                                       <div style={{
//                                         display: 'flex',
//                                         flexWrap: 'wrap',
//                                         gap: '10px',
//                                         fontSize: '0.7rem',
//                                         color: '#999'
//                                       }}>
//                                         <span>
//                                           <i className="pi pi-calendar" style={{ marginRight: '4px' }}></i>
//                                           {formatDate(material.created_at)}
//                                         </span>
//                                         {material.files.length > 0 && (
//                                           <span>
//                                             <i className="pi pi-paperclip" style={{ marginRight: '4px' }}></i>
//                                             {material.files.length} {material.files.length === 1 ? 'file' : 'files'}
//                                           </span>
//                                         )}
//                                         {material.video_links.length > 0 && (
//                                           <span>
//                                             <i className="pi pi-video" style={{ marginRight: '4px' }}></i>
//                                             {material.video_links.length} {material.video_links.length === 1 ? 'video' : 'videos'}
//                                           </span>
//                                         )}
//                                       </div>
//                                     </div>

//                                     <button
//                                       onClick={(e) => handleToggleModule(material.id, material.is_course_module_active ?? false, e)}
//                                       disabled={togglingModuleId === material.id}
//                                       style={{
//                                         padding: '6px 10px',
//                                         background: material.is_course_module_active ? '#4caf50' : '#e0e0e0',
//                                         color: material.is_course_module_active ? 'white' : '#666',
//                                         border: 'none',
//                                         borderRadius: '6px',
//                                         cursor: togglingModuleId === material.id ? 'not-allowed' : 'pointer',
//                                         fontSize: '0.7rem',
//                                         fontWeight: '600',
//                                         transition: 'all 0.2s ease',
//                                         display: 'flex',
//                                         alignItems: 'center',
//                                         gap: '4px',
//                                         flexShrink: 0
//                                       }}
//                                       title={material.is_course_module_active ? 'Remove from Course Modules' : 'Add to Course Modules'}
//                                     >
//                                       {togglingModuleId === material.id ? (
//                                         <>
//                                           <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.65rem' }}></i>
//                                           Updating...
//                                         </>
//                                       ) : (
//                                         <>
//                                           <i className={`pi ${material.is_course_module_active ? 'pi-check-circle' : 'pi-circle'}`} style={{ fontSize: '0.65rem' }}></i>
//                                           {material.is_course_module_active ? 'Module' : 'Add'}
//                                         </>
//                                       )}
//                                     </button>
//                                   </div>
//                                 </div>
//                               ))
//                               ) : (
//                                 <div style={{
//                                   padding: '20px',
//                                   textAlign: 'center',
//                                   background: 'white',
//                                   border: '1px solid #e9ecef',
//                                   borderRadius: '8px'
//                                 }}>
//                                   <i className="pi pi-inbox" style={{ fontSize: '2rem', color: '#ddd', marginBottom: '8px' }}></i>
//                                   <p style={{ margin: 0, fontSize: '0.8rem', color: '#999' }}>
//                                     No study materials in this chapter
//                                   </p>
//                                 </div>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               ))}
//             </div>
//           )}
//         </>
//       )}

//       {/* Details Modal */}
//       <ViewStudyMaterialModal
//         visible={showDetailsModal}
//         material={selectedMaterial}
//         onHide={() => setShowDetailsModal(false)}
//       />
//     </div>
//   );
// };

// export default StudyMaterials;